import morning_img_02 from "../../assets/images/morning-img-02-grey.png";
import DashboardLayout from "../../lib/components/layouts/dashboard";
import { Col, Image, Row } from "react-bootstrap";
import SimpleGreeting from "lib/components/common/simple-greeting";
import { AppContext } from "lib/contexts/app-context";
import { useContext, useEffect, useMemo, useState } from "react";
import SimpleDataTable from "lib/components/common/simple-data-table";
import { PAITENT_APPOINTMENT_BOOKING_DETAILS_COLUMNS, PAITENT_SESSION_BOOKING_DETAILS_COLUMNS } from "lib/constants/table-columns";
import SimpleCard from "lib/components/common/simple-card";

import usePatients from "lib/hooks/usePatients";
import {
  PiCalendarLight,
  PiClockLight,
  PiMoneyWavyLight,
} from "react-icons/pi";
import TabBar from "lib/components/common/tab-bar";
import FlexBox from "lib/components/common/flex-box";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleModal from "lib/components/common/simple-modal";
import { DEFAULT_PATIENT_APPOINTMENT_BOOKING_DETAILS, DEFAULT_PATIENT_SESSION_BOOKING_DETAILS } from "lib/constants/default-values";
import PatientBookingSummaryTile from "lib/components/common/patient/patient-booking-summary-tile";
import PatientSessionBookingSummaryTile from "../../lib/components/common/patient/patient-session-booking-summary-tile";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { BookingContext } from "lib/contexts/booking-context";
import useLocalStorage from "lib/hooks/useLocalStorage";
import { toast } from "sonner";

const PATIENT_APPOINTMENT_TABS: GeneralItem[] = [
  {
    id: 1,
    label: "Upcoming",
    icon: <PiCalendarLight />,
  },
  {
    id: 2,
    label: "Unpaid",
    icon: <PiMoneyWavyLight />,
  },
  {
    id: 3,
    label: "History",
    icon: <PiClockLight />,
  },
];

const TABS = {
  upcoming: 1,
  unpaid: 2,
  history: 3,
};

const PatientDashboard = () => {
  const navigate = useNavigate();
  const { getBookingsDetails, getSessionBookingsDetails, changeSessionBookingStatus, loading: patientsLoading } = usePatients();
  const { setValue } = useLocalStorage();

  const { appState } = useContext(AppContext);

  const [bookingsDetails, setBookingsDetails] = useState<
    PatientAppointmentBookingDetails[]
  >([]);
  const [sessionBookingsDetails, setSessionBookingsDetails] = useState<
    SessionBookingDetails[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBookingDetails, setShowBookingDetails] = useState<boolean>(false);
  const [selectedBookingDetails, setSelectedBookingDetails] =
    useState<PatientAppointmentBookingDetails>(
      DEFAULT_PATIENT_APPOINTMENT_BOOKING_DETAILS
    );
  const [showSessionBookingDetails, setShowSessionBookingDetails] = useState<boolean>(false);
  const [selectedSessionBookingDetails, setSelectedSessionBookingDetails] =
    useState<PatientSessionBookingDetails>(
      DEFAULT_PATIENT_SESSION_BOOKING_DETAILS
    );

  const selectedTab = searchParams.get("tab") || "upcoming";
  const selectedTabId = TABS[selectedTab] ?? 0;

  const setSelectedTabId = (tabId: number) => {
    searchParams.set(
      "tab",
      Object.keys(TABS).find((key) => TABS[key] === tabId) || "clinics"
    );
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (appState?.user_id) {
      getBookingsDetails(appState.user_id, (bookings) => {
        setBookingsDetails(bookings);
      });
      getSessionBookingsDetails(appState.user_id, (bookingsessions) => {
        setSessionBookingsDetails(bookingsessions);
      });
    }
  }, []);

  const pendingAppointments = useMemo(() => {
    return bookingsDetails.filter((booking) => booking.status === "pending");
  }, [bookingsDetails]);

  const scheduledAppointments = useMemo(() => {
    return bookingsDetails.filter((booking) => booking.status === "scheduled");
  }, [bookingsDetails]);

  const completedAppointments = useMemo(() => {
    return bookingsDetails.filter((booking) => booking.status === "completed");
  }, [bookingsDetails]);

  const pendingSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "pending");
  }, [sessionBookingsDetails]);

  const scheduledSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "scheduled");
  }, [sessionBookingsDetails]);

  const completedSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "completed");
  }, [sessionBookingsDetails]);

  // Handler to update session booking status using API
  const onChangeSessionBookingStatus = async (sessionId: string, newStatus: string) => {
    try {
      await changeSessionBookingStatus(appState.user_id, sessionId, newStatus);
      setSessionBookingsDetails((prev) =>
        prev.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus } : session
        )
      );
      toast.success(`Session status updated to '${newStatus}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  return (
    <DashboardLayout>
      <div className="good-morning-blk mt-2">
        <Row>
          <Col md={8}>
            <div className="morning-user">
              <SimpleGreeting name={appState?.name} />
              <p>Have a nice day at work</p>
            </div>
          </Col>
          <Col md={4}>
            <div className="morning-img">
              <Image src={morning_img_02} alt="" />
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col sm={12}>
          <SimpleCard>
            <div className="d-md-flex align-items-center justify-content-between mb-3">
              <h4 className="head-title">
                {selectedTabId === 1 ? "Appointments" : "Appointment History"}
              </h4>
              <TabBar
                tabs={PATIENT_APPOINTMENT_TABS}
                selectedTabId={selectedTabId}
                onTabChange={setSelectedTabId}
              />
            </div>
            {selectedTabId === 1 && (
              <SimpleDataTable
                data={scheduledAppointments}
                columns={PAITENT_APPOINTMENT_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                onRowClick={(row: PatientAppointmentBookingDetails) => {
                  setSelectedBookingDetails(row);
                  setShowBookingDetails(true);
                }}
              />
            )}

            {selectedTabId === 2 && (
              <SimpleDataTable
                data={pendingAppointments}
                columns={PAITENT_APPOINTMENT_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                onRowClick={(row: PatientAppointmentBookingDetails) => {
                  // Implement logic to handle unpaid appointments
                  // navigate(`${PAGE_ROUTES.PATIENT.BookingFailure}`, {
                  //   state: { transactionId: row.transactionId },
                  // });
                  setSelectedBookingDetails(row);
                  setShowBookingDetails(true);
                }}
              />
            )}

            {selectedTabId === 3 && (
              <SimpleDataTable
                data={completedAppointments}
                columns={PAITENT_APPOINTMENT_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                onRowClick={(row: PatientAppointmentBookingDetails) => {
                  setSelectedBookingDetails(row);
                  setShowBookingDetails(true);
                }}
              />
            )}
          </SimpleCard>
        </Col>
      </Row>
      <div className="mt-4">
        <Row>
          <Col sm={12} className="mb-4">
            <SimpleCard>
              <div className="d-md-flex align-items-center justify-content-between mb-3">
                <h4 className="head-title">
                  {selectedTabId === 1 ? "Therapy Sessions" : "Therapy Sessions History"}
                </h4>
                <TabBar
                  tabs={PATIENT_APPOINTMENT_TABS}
                  selectedTabId={selectedTabId}
                  onTabChange={setSelectedTabId}
                />
              </div>
              {selectedTabId === 1 && (
                <SimpleDataTable
                  data={scheduledSessionsBookings}
                  columns={PAITENT_SESSION_BOOKING_DETAILS_COLUMNS}
                  isLoading={patientsLoading}
                  defaultSortFieldId="date"
                  defaultSortAsc={true}
                  onRowClick={(row: PatientSessionBookingDetails) => {
                    setSelectedSessionBookingDetails(row);
                    setShowSessionBookingDetails(true);
                  }}
                />
              )}

              {selectedTabId === 2 && (
                <SimpleDataTable
                  data={pendingSessionsBookings}
                  columns={PAITENT_SESSION_BOOKING_DETAILS_COLUMNS}
                  isLoading={patientsLoading}
                  defaultSortFieldId="date"
                  defaultSortAsc={true}
                  onRowClick={(row: PatientSessionBookingDetails) => {
                    setSelectedSessionBookingDetails(row);
                    setShowSessionBookingDetails(true);
                  }}
                />
              )}

              {selectedTabId === 3 && (
                <SimpleDataTable
                  data={completedSessionsBookings}
                  columns={PAITENT_SESSION_BOOKING_DETAILS_COLUMNS}
                  isLoading={patientsLoading}
                  defaultSortFieldId="date"
                  defaultSortAsc={true}
                  onRowClick={(row: PatientSessionBookingDetails) => {
                    setSelectedSessionBookingDetails(row);
                    setShowSessionBookingDetails(true);
                  }}
                />
              )}
            </SimpleCard>
          </Col>
        </Row>
      </div>
      <SimpleModal
        open={showBookingDetails}
        close={() => setShowBookingDetails(false)}
        title="Booking Details"
        size="sm"
      >
        <PatientBookingSummaryTile
          fromTime={selectedBookingDetails.fromTime}
          {...selectedBookingDetails}
        />
      </SimpleModal>
      <SimpleModal
        open={showSessionBookingDetails}
        close={() => setShowSessionBookingDetails(false)}
        title="Session Details"
        size="sm"
      >
        <PatientSessionBookingSummaryTile
          fromTime={selectedSessionBookingDetails.fromTime}
          {...selectedSessionBookingDetails}
          onChangeSessionBookingStatus={onChangeSessionBookingStatus}
        />
      </SimpleModal>
    </DashboardLayout>
  );
};

export default PatientDashboard;
